import React, { useEffect, useState } from "react";
import { QrCode, Camera, Sparkles, Smartphone, ChevronDown } from "lucide-react";

const ReaderLink = ({ href, label, hint, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="flex items-start gap-2 group rounded-md border border-borderline/70 bg-white/70 hover:bg-white hover:border-terracotta/60 transition-colors px-3 py-2"
  >
    <Smartphone size={14} className="text-terracotta mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-[12px] font-medium text-ink leading-tight group-hover:text-terracotta transition-colors">
        {label}
      </p>
      <p className="text-[10px] text-ink2/80 leading-tight mt-0.5">{hint}</p>
    </div>
  </a>
);

/** Sezione footer QR / AR — chiusa di default, tendina al clic. */
export default function FooterTurismoIntelligente() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="border-t border-borderline bg-parchment/60" data-testid="footer-turismo-intelligente">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="footer-turismo-intelligente-panel"
        className="w-full max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4 text-left hover:bg-parchment2/40 transition-colors"
        data-testid="footer-turismo-intelligente-toggle"
      >
        <div className="min-w-0">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-terracotta mb-1 inline-flex items-center gap-2">
            <QrCode size={11} aria-hidden />
            Turismo Intelligente
          </p>
          <p className="font-display text-lg sm:text-xl text-ink leading-snug">
            QR Code e <span className="text-terracotta">Realtà Aumentata</span>
            <span className="hidden sm:inline text-ink2 font-sans text-sm font-normal">
              {" "}
              — guida lettori e 3 passi
            </span>
          </p>
        </div>
        <span
          className={`shrink-0 flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-ink2 ${
            open ? "text-terracotta" : ""
          }`}
        >
          {open ? "Chiudi" : "Apri"}
          <ChevronDown
            size={18}
            className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
        </span>
      </button>

      {open && (
        <div
          id="footer-turismo-intelligente-panel"
          className="border-t border-borderline/80"
          data-testid="footer-turismo-intelligente-panel"
        >
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <h3 className="font-display text-2xl text-ink leading-tight mb-2">
                Scansiona un QR Code,
                <br />
                scopri il borgo in <span className="text-terracotta">Realtà Aumentata</span>.
              </h3>
              <p className="text-sm text-ink2 leading-relaxed max-w-md">
                Ogni monumento, frazione e capsula del tempo del Network 6amico ha un QR Code unico.
                Inquadralo con la fotocamera o un&apos;app lettore: ti porterà alla scheda multilingua,
                ai contenuti AR (modelli 3D, video storici) e all&apos;audio-guida.
              </p>

              <div className="mt-5 space-y-2.5" data-testid="footer-qr-howto">
                <h4 className="font-mono text-[10px] tracking-widest uppercase text-ink2 mb-1">
                  Come si usa · 3 passi
                </h4>
                <ol className="space-y-2">
                  <li className="flex items-start gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-terracotta text-parchment text-[10px] font-mono flex items-center justify-center">
                      1
                    </span>
                    <p className="text-[12px] text-ink2 leading-relaxed">
                      <strong className="text-ink">Inquadra</strong> il QR Code (placca, totem o cartolina) —
                      la fotocamera basta su iPhone e gran parte degli Android moderni.
                    </p>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-terracotta text-parchment text-[10px] font-mono flex items-center justify-center">
                      2
                    </span>
                    <p className="text-[12px] text-ink2 leading-relaxed">
                      <strong className="text-ink">Tocca la notifica</strong> che appare in alto: si aprirà{" "}
                      <span className="font-mono">6amico.com</span> alla scheda del bene.
                    </p>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-terracotta text-parchment text-[10px] font-mono flex items-center justify-center">
                      3
                    </span>
                    <p className="text-[12px] text-ink2 leading-relaxed">
                      <strong className="text-ink">Esplora</strong> contenuti, modelli 3D, audio-guide e prenota
                      visite o stanze direttamente dalla scheda.
                    </p>
                  </li>
                </ol>
              </div>
            </div>

            <div className="lg:col-span-7">
              <h4 className="font-mono text-[10px] tracking-widest uppercase text-ink2 mb-3 inline-flex items-center gap-2">
                <Sparkles size={11} className="text-gold" aria-hidden />
                App lettori QR · Scarica gratis
              </h4>
              <div className="grid sm:grid-cols-2 gap-2.5" data-testid="footer-qr-readers">
                <ReaderLink
                  testid="footer-qr-reader-camera-ios"
                  href="https://support.apple.com/it-it/guide/iphone/iphd2d4c5b3e/ios"
                  label="📱 Fotocamera iOS · nativa"
                  hint="iPhone (iOS 11+) — apri la Fotocamera, inquadra il QR, tocca la notifica gialla."
                />
                <ReaderLink
                  testid="footer-qr-reader-google-lens"
                  href="https://play.google.com/store/apps/details?id=com.google.ar.lens"
                  label="🔍 Google Lens · Android"
                  hint="Pre-installato su gran parte degli Android — riconosce monumenti, testi, opere d'arte."
                />
                <ReaderLink
                  testid="footer-qr-reader-android-camera"
                  href="https://developer.android.com/about/versions/13/features/qrscanner?hl=it"
                  label="📸 Fotocamera Android · nativa"
                  hint="Android 13+ — apri Fotocamera, inquadra il QR e tocca la notifica. Su Pixel/Samsung/Xiaomi è integrata."
                />
                <ReaderLink
                  testid="footer-qr-reader-qrcode-monkey"
                  href="https://play.google.com/store/apps/details?id=com.gamma.scan"
                  label="⚡ QR & Barcode Scanner"
                  hint="Alternativa gratuita per Android meno recenti — apertura istantanea, no pubblicità invasiva."
                />
                <ReaderLink
                  testid="footer-qr-reader-qrcode-ios"
                  href="https://apps.apple.com/it/app/qr-code-reader/id1200318119"
                  label="⚡ QR Code Reader · iOS"
                  hint="App alternativa per iPad o iOS più datati — funziona offline, salva la cronologia."
                />
                <div
                  className="rounded-md border border-dashed border-gold/50 bg-gold/5 px-3 py-2 flex items-start gap-2"
                  data-testid="footer-qr-ar-tip"
                >
                  <Camera size={14} className="text-gold mt-0.5 shrink-0" aria-hidden />
                  <div>
                    <p className="text-[11px] font-medium text-ink leading-tight">Esperienza QR-AR</p>
                    <p className="text-[10px] text-ink2 leading-tight mt-0.5">
                      Per i monumenti con modello 3D usa <strong>Safari (iOS)</strong> o{" "}
                      <strong>Chrome (Android)</strong>: il QR aprirà l&apos;AR-viewer in pochi secondi, senza app
                      aggiuntive.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-ink2/70 italic mt-3 leading-relaxed">
                I link rimandano agli store ufficiali (Apple App Store · Google Play). 6amico non riceve
                commissioni: la scelta del lettore è libera.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
